import { UserRoundCheck } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';

type PermissionState = NotificationPermission | 'unsupported';

const NOTIF_DEDUP_PREFIX = 'notif_shown:';
const NOTIF_DEDUP_TTL_MS = 60_000; // 1 minute

/**
 * Cross-tab deduplication via localStorage.
 * Returns true if this is the first tab to claim the notification with the
 * given tag, meaning it should show the browser popup. Other tabs that call
 * this with the same tag will see the key already exists and return false.
 */
function claimNotification(tag: string): boolean {
  const key = `${NOTIF_DEDUP_PREFIX}${tag}`;
  try {
    if (localStorage.getItem(key)) return false;
    localStorage.setItem(key, Date.now().toString());

    // Prune stale entries to prevent localStorage bloat
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (k?.startsWith(NOTIF_DEDUP_PREFIX)) {
        const ts = Number(localStorage.getItem(k));
        if (Date.now() - ts > NOTIF_DEDUP_TTL_MS) {
          localStorage.removeItem(k);
        }
      }
    }
    return true;
  } catch {
    // localStorage unavailable — allow notification rather than silently dropping it
    return true;
  }
}

interface NotificationOptions {
  body?: string;
  onClick?: () => void;
  /** When provided, only the first tab to claim this tag shows the notification. */
  tag?: string;
}

function NotificationToast({
  id,
  title,
  body,
  onClick,
}: {
  id: string | number;
  title: string;
  body?: string;
  onClick?: () => void;
}) {
  return (
    <div className="flex gap-3 w-full items-start p-4">
      <span className="flex items-center justify-center size-8 rounded-lg bg-primary/10 shrink-0 mt-0.5">
        <UserRoundCheck className="size-4 text-primary" />
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground">{title}</p>
        {body && <p className="text-sm text-foreground mt-0.5">{body}</p>}
        <div className="flex items-center gap-3 mt-2">
          {onClick && (
            <Button variant="default" size="sm" className="h-7 text-xs" onClick={onClick}>
              View
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            onClick={() => toast.dismiss(id)}
          >
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Hook that provides browser Notification API with toast fallback.
 *
 * - If browser notifications are granted, shows a native notification.
 * - If permission is 'default', requests permission on first use.
 * - If denied or unsupported, falls back to a sonner toast.
 */
export function useWebNotification() {
  const [permission, setPermission] = useState<PermissionState>(() => {
    if (typeof Notification === 'undefined') return 'unsupported';
    return Notification.permission;
  });

  // Track permission changes (e.g. user changes in browser settings)
  useEffect(() => {
    if (typeof Notification === 'undefined') return;
    setPermission(Notification.permission);
  }, []);

  const pendingRef = useRef(false);
  // Keep a persistent reference to the active notification so the browser
  // doesn't garbage-collect it (which would silently break onclick).
  const activeNotificationRef = useRef<Notification | null>(null);

  // Clean up any active notification when the component unmounts.
  useEffect(() => {
    return () => {
      activeNotificationRef.current?.close();
      activeNotificationRef.current = null;
    };
  }, []);

  const requestPermission = useCallback(async () => {
    if (typeof Notification === 'undefined') {
      setPermission('unsupported');
      return 'unsupported' as PermissionState;
    }
    if (Notification.permission !== 'default') {
      setPermission(Notification.permission);
      return Notification.permission;
    }
    if (pendingRef.current) return Notification.permission;

    pendingRef.current = true;
    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      return result;
    } finally {
      pendingRef.current = false;
    }
  }, []);

  const showNativeNotification = useCallback((title: string, options?: NotificationOptions) => {
    activeNotificationRef.current?.close();

    const notification = new Notification(title, {
      body: options?.body,
      icon: '/images/company/hms-header-logo.png',
      tag: options?.tag,
      requireInteraction: !!options?.onClick,
    });

    activeNotificationRef.current = notification;

    notification.onclick = event => {
      // Prevent default browser behavior (e.g. focusing the notification's tab)
      // so we can control navigation ourselves.
      event.preventDefault();
      window.focus();
      options?.onClick?.();
      notification.close();
      activeNotificationRef.current = null;
    };
  }, []);

  const showNotification = useCallback(
    async (title: string, options?: NotificationOptions) => {
      // Cross-tab dedup: if a tag is provided, only show in the first tab that claims it
      if (options?.tag && !claimNotification(options.tag)) return;

      let perm = permission;

      // Auto-request if still default
      if (perm === 'default') {
        perm = await requestPermission();
      }

      if (perm === 'granted') {
        showNativeNotification(title, options);
      } else {
        // Fallback to custom toast with notification icon and actions
        toast.custom(
          id => (
            <NotificationToast
              id={id}
              title={title}
              body={options?.body}
              onClick={options?.onClick}
            />
          ),
          { duration: 30_000 },
        );
      }
    },
    [permission, requestPermission, showNativeNotification],
  );

  return { permission, requestPermission, showNotification };
}
